/**
 * What a thread is for, and therefore who is on the other end of it.
 *
 * One primitive rather than two, because everything that is not the
 * participant list — the sequence, idempotency, read cursors, the inbox —
 * has to behave identically, and duplicated machinery drifts. Support is
 * not a second messaging stack; it is a thread whose other party is Zuruflex.
 */
export const THREAD_KINDS = [
    /// A renter and the partner they hire from. One per pair, for good,
    /// however many cars and however many hires.
    'DIRECT',
    /// Either of them, and Zuruflex.
    'SUPPORT',
];
/**
 * Where the conversation is actually happening.
 *
 * The thread does not behave differently per channel — same routes, same
 * sequence, same inbox row. This says where a reply has to be *delivered*,
 * which is the only thing that differs.
 */
export const THREAD_CHANNELS = ['WEB', 'WHATSAPP', 'EMAIL'];
/**
 * `WAITING` is answered-and-waiting-on-them, which is not resolved.
 * Collapsing the two would make both unmeasurable: first response and
 * resolution are different clocks and are reported on separately.
 *
 * A `DIRECT` thread only ever uses `OPEN` and `CLOSED` — there is no
 * agent to be waiting on, and nobody resolves a conversation with the
 * person they are hiring a car from.
 */
export const THREAD_STATUSES = ['OPEN', 'WAITING', 'SNOOZED', 'RESOLVED', 'CLOSED'];
/** The statuses an agent may set by hand. `CLOSED` is reached by the sweep. */
export const THREAD_SETTABLE_STATUSES = ['OPEN', 'WAITING', 'SNOOZED', 'RESOLVED'];
export const THREAD_PRIORITIES = ['LOW', 'NORMAL', 'HIGH', 'URGENT'];
/**
 * Coarse enough that an agent picks one without thinking, fine enough to
 * route on. Anything sharper belongs in tags.
 */
export const THREAD_TOPICS = [
    'BOOKING',
    'PAYMENT',
    'VEHICLE',
    'ACCOUNT',
    'PARTNER',
    'OTHER',
];
/** Which realm a member belongs to. */
export const THREAD_MEMBER_KINDS = ['CUSTOMER', 'PARTNER_USER', 'STAFF'];
/**
 * Who wrote one message.
 *
 * `BOT` exists so the concierge's last turns can be carried into a thread
 * at handoff: an agent who can read what the bot already said does not
 * have to ask the customer to start again.
 *
 * `SYSTEM` is the platform's own line in the transcript — on a `DIRECT`
 * thread, the divider naming the hire the conversation has moved on to.
 * Rendered as a marker between bubbles rather than as one.
 */
export const THREAD_AUTHOR_KINDS = ['CUSTOMER', 'PARTNER_USER', 'STAFF', 'SYSTEM', 'BOT'];
/**
 * Longer than a chat message and much shorter than an essay. WhatsApp
 * truncates around 4096, and a reply that arrives in three pieces because
 * somebody pasted a contract is worse than one that was refused.
 */
export const MAX_MESSAGE_CHARS = 4000;
/**
 * How long after sending its author may still change a message.
 *
 * Long enough to fix a typo or a wrong time, short enough that a reply
 * cannot be rewritten after the other side has acted on it. Deleting has no
 * window: withdrawing something is always allowed, and staff keep the
 * original either way.
 */
export const MESSAGE_EDIT_WINDOW_MS = 15 * 60_000;
/**
 * What stands in for a contact detail that was taken out of a message.
 *
 * Here rather than in `@zuruflex/core` because it is part of the shape of a
 * body on the wire: the reader has to render it, and the design system
 * cannot depend on the rules package to find out what it is looking at.
 */
export const REDACTION_MARK = '[hidden]';
/** How many bot turns are carried into a thread when a handoff opens it. */
export const HANDOFF_CONTEXT_MESSAGES = 20;
/**
 * The upload limit the presign route enforces, mirrored so a UI can refuse
 * a file before spending somebody's data allowance on it.
 */
export const MAX_ATTACHMENTS_PER_MESSAGE = 5;
/**
 * The long edge of the small copy a sender uploads beside a photograph.
 *
 * 640 at a middling quality lands near 60KB, about a twelfth of the 2400px
 * original. 256 is visibly soft at the width a bubble draws it; 1024 is not
 * worth the bytes over simply asking for the original.
 */
export const THUMBNAIL_MAX_EDGE = 640;
/** Its JPEG quality. Soft under a pinch is fine; that is what the original is for. */
export const THUMBNAIL_QUALITY = 0.6;
/**
 * Whether the caller may still change this message. The API applies the
 * same test, so a UI offering Edit is never offering something refused.
 *
 * App channel only: a message that went out over WhatsApp is already on
 * somebody's phone, and editing our copy would make the transcript lie.
 */
export function canEditMessage(message, now = Date.now()) {
    return (canDeleteMessage(message) &&
        now - new Date(message.createdAt).getTime() < MESSAGE_EDIT_WINDOW_MS);
}
/** Whether the caller may withdraw this message. No time limit. */
export function canDeleteMessage(message) {
    return (message.isMine &&
        !message.deletedAt &&
        message.channel === 'WEB' &&
        message.authorKind !== 'SYSTEM' &&
        message.authorKind !== 'BOT');
}
/**
 * The scores a thread can be given, as a value and not a `1 | 2 | 3 | 4 | 5`
 * union, because both phone apps draw the prompt by mapping over the row
 * of faces and a type cannot be iterated.
 *
 * Five points rather than a thumb up or down: two buckets cannot tell a
 * thread that was merely handled apart from one somebody was grateful
 * for, and that difference is the whole reason to ask.
 */
export const CSAT_SCORES = [1, 2, 3, 4, 5];
